import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useCenter } from "../../../center";
import { PatientOwnershipBadge } from "./PatientOwnership";
import { motion, AnimatePresence } from "framer-motion";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import { deleteCouplePatient, listTherapistCouples } from "../../application/patients";
import { Trash2, Users, Pencil } from "lucide-react";
import { useNavigate } from "react-router";

import { CouplePatient } from "../../domain/CouplePatient";
import { getAvatarColor } from "../../../../shared/utils/avatar";

interface CouplesListProps {
    therapistID: string;
    pageSize?: number;
    filters?: { name: string };
}

const listVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.05
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: "easeOut" as const }
    }
};

export default function CouplesList({ therapistID, pageSize = 10, filters }: CouplesListProps) {
    const { t } = useTranslation();
    // Whose each couple is — only meaningful inside a centre.
    const { center } = useCenter();
    const [couples, setCouples] = useState<CouplePatient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);

    const navigate = useNavigate();

    const fetchCouples = useCallback(async () => {
        if (!therapistID) return;
        try {
            setLoading(true);
            const data = await listTherapistCouples(therapistID);
            setCouples(data);
            setTotal(data.length); // The backend doesn't support pagination for couples yet in the router, but we can slice locally if needed
        } catch (err) {
            console.error("Error loading couple patients:", err);
            setError(t("patient.errorLoading"));
        } finally {
            setLoading(false);
        }
    }, [therapistID, t]);

    useEffect(() => {
        fetchCouples();
    }, [fetchCouples]);

    const handleDelete = async (id: string) => {
        if (!confirm(t("common.confirmDelete"))) return;
        setDeletingId(id);
        try {
            await deleteCouplePatient(id);
            setCouples((prev) => prev.filter((c) => c.id !== id));
        } catch (err) {
            console.error("Error deleting couple patient:", err);
            alert(t("patient.errorDeleting"));
        } finally {
            setDeletingId(null);
        }
    };

    const handlePrevPage = () => setOffset((prev) => Math.max(prev - pageSize, 0));
    const handleNextPage = () => setOffset((prev) => prev + pageSize);



    const filteredCouples = couples.filter(couple => {
        if (!filters?.name) return true;
        const searchName = filters.name.toLowerCase();

        if (couple.display_name?.toLowerCase().includes(searchName)) return true;

        if (couple.patient_1) {
            const p1Name = `${couple.patient_1.first_name} ${couple.patient_1.last_name || ''}`.toLowerCase();
            if (p1Name.includes(searchName)) return true;
        }
        if (couple.patient_2) {
            const p2Name = `${couple.patient_2.first_name} ${couple.patient_2.last_name || ''}`.toLowerCase();
            if (p2Name.includes(searchName)) return true;
        }

        return false;
    });

    useEffect(() => {
        setTotal(filteredCouples.length);
        setOffset(0);
    }, [filteredCouples.length, filters?.name]);

    if (loading) {
        return (
            <div className="flex justify-center py-10 text-content-tertiary">
                {t("patient.loading")}
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-center text-red-500 dark:text-red-400">
                {error}
            </div>
        );
    }

    if (!filteredCouples.length) {
        return (
            <div className="p-4 text-center text-content-tertiary">
                {t("patient.notFound")}
            </div>
        );
    }

    const paginatedCouples = filteredCouples.slice(offset, offset + pageSize);

    return (
        <motion.div
            variants={listVariants}
            initial="hidden"
            animate="visible"
            className="space-y-4"
        >
            <div className="grid grid-cols-1 gap-4">
                <AnimatePresence mode="popLayout">
                    {paginatedCouples.map((couple) => (
                        <motion.div
                            layout
                            variants={itemVariants}
                            key={couple.id}
                            className="group relative flex flex-col sm:flex-row items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl shadow-theme-xs hover:border-brand-500/50 hover:shadow-raised transition-all duration-300 dark:bg-gray-900/40 dark:border-gray-800 dark:hover:border-brand-500/50"
                        >
                            <div className="flex flex-1 items-center gap-4 w-full">
                                <div
                                    className={`flex items-center justify-center w-12 h-12 rounded-full shrink-0 text-sm font-bold uppercase transition-transform group-hover:scale-110 ${getAvatarColor(
                                        couple.id
                                    )}`}
                                >
                                    <Users className="w-6 h-6" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="block group-hover:text-brand-500 transition-colors cursor-pointer" onClick={() => navigate(`/couple-patient/${couple.id}`)}>
                                        <h3 className="text-base font-semibold text-content-primary truncate">
                                            {couple.display_name}
                                        </h3>
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-content-tertiary">
                                        <span>
                                            {couple.patient_1 && couple.patient_2
                                                ? `${couple.patient_1.first_name} ${couple.patient_1.last_name} & ${couple.patient_2.first_name} ${couple.patient_2.last_name}`
                                                : t("patient.coupleSession", "Couple Session")
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {center && (
                                <PatientOwnershipBadge
                                    heldByCenter={Boolean(couple.patient_1?.center_id)}
                                    className="mt-3 sm:mt-0 sm:ml-4"
                                />
                            )}

                            <div className="flex items-center gap-2 mt-4 sm:mt-0 ml-auto sm:ml-4">
                                <button
                                    className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 transition-all"
                                    onClick={() => navigate(`/couple-patient/${couple.id}`)}
                                    title={t("common.edit")}
                                >
                                    <Pencil className="w-5 h-5" />
                                </button>
                                <button
                                    className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all"
                                    onClick={() => handleDelete(couple.id)}
                                    disabled={deletingId === couple.id}
                                    title={t("common.delete")}
                                >
                                    {deletingId === couple.id ? (
                                        <span className="w-5 h-5 flex items-center justify-center text-xs animate-pulse">...</span>
                                    ) : (
                                        <Trash2 className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            <Pagination
                offset={offset}
                total={total}
                pageSize={pageSize}
                onPrev={handlePrevPage}
                onNext={handleNextPage}
            />
        </motion.div>
    );
}
